import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Layers,
  FileText,
  Users,
  Play,
  Zap,
  PieChart,
  Table2,
  FileSpreadsheet,
  Percent,
  Download,
  Search,
  BarChart3,
  ClipboardList,
  LayoutList,
  ShieldCheck,
  Calculator,
  Sparkles,
  RefreshCw,
  Clock,
  AlertCircle,
  Activity,
} from "lucide-react";
import FeatureCard from "./components/FeatureCard";
import { fetchPayrollHomeStats } from "./payrollHomeStats";
import "./PayrollHome.css";

const MODULES = [
  {
    id: "salary-configuration",
    title: "Payroll Settings",
    description: "Pay cycle, processing day, payment date, attendance cycle, and LOP rules.",
    route: "/salary-v2/setup",
    icon: Sparkles,
    section: "salary",
    badge: "frequent",
  },
  {
    id: "overtime-works",
    title: "Overtime Works",
    description: "Enable OT, hours source, OT pay calculation, eligibility, and OT payroll approval.",
    route: "/salary-v2/overtime",
    icon: Clock,
    section: "salary",
    badge: "new",
  },
  {
    id: "extra-work-ot",
    title: "Premium Works",
    description: "Sunday work, holiday work, premium multipliers, and Comp-Off policy.",
    route: "/salary-v2/extra-work",
    icon: Zap,
    section: "salary",
    badge: "new",
  },
  {
    id: "salary-configuration-advanced",
    title: "Payroll Configuration",
    description: "Earnings, deductions, benefits, formulas, groups, and statutory mapping.",
    route: "/salary-v2/components",
    icon: Layers,
    section: "salary",
  },
  {
    id: "salary-templates",
    title: "Salary Templates",
    description: "Build salary structures using components.",
    route: "/salary/templates",
    icon: FileText,
    section: "salary",
  },
  {
    id: "employee-salary-structure",
    title: "Employee Salary Structure",
    description: "Assign salary templates to employees.",
    route: "/employeesalarystructure",
    icon: Users,
    section: "salary",
    badge: "important",
  },
  {
    id: "ctc-planner",
    title: "CTC Salary Planner",
    description: "Enter annual CTC — auto-split Basic, HRA, PF, ESI (standalone, no payroll impact).",
    route: "/tools/ctc-planner",
    icon: Calculator,
    section: "salary",
    badge: "new",
  },
  {
    id: "payroll-process",
    title: "Payroll Process",
    description: "Create pay periods and run payroll calculation from one workspace.",
    route: "/payroll-process",
    icon: Zap,
    section: "processing",
    badge: "important",
  },
  {
    id: "payroll-runs",
    title: "Payroll Runs",
    description: "History by month, run status, net pay, and employee breakdowns.",
    route: "/payroll-runs",
    icon: Play,
    section: "processing",
    badge: "frequent",
  },
  {
    id: "payroll-batch",
    title: "Batch Process Payroll",
    description: "Queue multiple payroll runs and track async status.",
    route: "/payroll-batch",
    icon: LayoutList,
    section: "processing",
  },
  {
    id: "payroll-finalize",
    title: "Finalize Payroll",
    description: "Verify, approve, and lock payroll (Phase 4).",
    route: "/payroll-finalize",
    icon: ShieldCheck,
    section: "processing",
    badge: "important",
  },
  {
    id: "payroll-summary",
    title: "Payroll Summary",
    description: "View payroll totals and statistics.",
    route: "/summary",
    icon: PieChart,
    section: "processing",
  },
  {
    id: "payroll-register",
    title: "Payroll View",
    description: "View employee payroll earnings and deductions.",
    route: "/reports/payroll",
    icon: Table2,
    section: "processing",
  },
  {
    id: "payslips",
    title: "Payslips",
    description: "View or download payslips (locked payroll only).",
    route: "/payslip",
    icon: Download,
    section: "processing",
  },
  {
    id: "salary-statement",
    title: "Salary Statement",
    description: "Component-wise salary breakdown.",
    route: "/salary-statement",
    icon: FileSpreadsheet,
    section: "reports",
  },
  {
    id: "tds-summary",
    title: "TDS Summary",
    description: "View taxable salary and TDS deductions.",
    route: "/tds-summary",
    icon: Percent,
    section: "reports",
  },
];

const SECTIONS = [
  {
    id: "salary",
    title: "Salary Setup",
    subtitle: "Components, templates, and employee compensation structures.",
  },
  {
    id: "processing",
    title: "Payroll Processing",
    subtitle: "Periods, runs, processing, and operational payroll outputs.",
  },
  {
    id: "reports",
    title: "Reports & Insights",
    subtitle: "Statements, compliance views, and payroll analytics.",
  },
];

const STAT_ICONS = {
  employees: Users,
  runs: Activity,
  processed: Clock,
  pending: AlertCircle,
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, delayChildren: 0.03 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.26, ease: [0.16, 1, 0.3, 1] },
  },
};

function formatAsOf(iso) {
  if (!iso) return "";
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function PayrollHome() {
  const nav = useNavigate();
  const [query, setQuery] = useState("");

  const {
    data: stats,
    isLoading: statsLoading,
    isFetching,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ["payroll-home-stats"],
    queryFn: fetchPayrollHomeStats,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const quickStats = useMemo(() => {
    const loading = statsLoading && !stats;
    return [
      {
        key: "employees",
        label: "Total Employees",
        value: loading ? null : String(stats?.totalEmployees ?? 0),
        hint: stats?.periodLabel
          ? `Latest payroll: ${stats.periodLabel}`
          : "Active in organisation",
        icon: STAT_ICONS.employees,
      },
      {
        key: "runs",
        label: "Active Payroll Runs",
        value: loading ? null : String(stats?.activePayrollRuns ?? 0),
        hint:
          stats?.openPayPeriods != null
            ? `${stats.openPayPeriods} open pay period${stats.openPayPeriods === 1 ? "" : "s"}`
            : "Draft or in-progress runs",
        icon: STAT_ICONS.runs,
      },
      {
        key: "processed",
        label: "This Month Processed",
        value: loading ? null : String(stats?.thisMonthProcessed ?? 0),
        hint: "Completed runs this month",
        icon: STAT_ICONS.processed,
      },
      {
        key: "pending",
        label: "Pending Tasks",
        value: loading ? null : String(stats?.pendingTasks ?? 0),
        hint: "Alerts + open payroll actions",
        icon: STAT_ICONS.pending,
      },
    ];
  }, [stats, statsLoading]);

  const filteredModules = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return MODULES;
    return MODULES.filter(
      (m) =>
        m.title.toLowerCase().includes(q) ||
        m.description.toLowerCase().includes(q)
    );
  }, [query]);

  const sectionsWithItems = useMemo(() => {
    return SECTIONS.map((section) => ({
      ...section,
      items: filteredModules.filter((m) => m.section === section.id),
    })).filter((s) => s.items.length > 0);
  }, [filteredModules]);

  const updatedLabel = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div className="ph-root min-h-screen bg-[#eef2f7] py-4 sm:py-5 md:py-6 px-4 sm:px-5 md:px-6">
      <div className="ph-page">
        <motion.header
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          className="ph-hero"
        >
          <div className="ph-hero-row">
            <div className="ph-hero-main">
              <div className="ph-hero-icon" aria-hidden>
                <BarChart3 size={22} strokeWidth={1.75} />
              </div>
              <div>
                <div className="ph-hero-badge">
                  <Sparkles size={10} />
                  Payroll Hub
                </div>
                <h1 className="ph-hero-title">Payroll Management</h1>
                <p className="ph-hero-sub">
                  Configure salary structures, run payroll, and review reports from one place.
                  {stats?.asOf ? ` Data as of ${formatAsOf(stats.asOf)}.` : ""}
                </p>
              </div>
            </div>

            <div className="ph-hero-actions">
              {!statsLoading && updatedLabel ? (
                <span className="ph-live-pill" title="Auto-refreshes every minute">
                  <span className="ph-live-dot" aria-hidden />
                  Live · {updatedLabel}
                </span>
              ) : null}
              <div className="ph-search-wrap">
                <Search size={16} aria-hidden />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search modules…"
                  className="ph-search-input"
                  aria-label="Filter payroll modules"
                />
              </div>
              <button
                type="button"
                className="ph-refresh-btn"
                onClick={() => refetch()}
                disabled={isFetching}
                title="Refresh live stats"
              >
                <RefreshCw size={15} className={isFetching ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>
          </div>
        </motion.header>

        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, delay: 0.05 }}
          className="ph-stats"
          aria-label="Payroll overview statistics"
        >
          {quickStats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.key} className="ph-stat-card">
                <div className="ph-stat-top">
                  <p className="ph-stat-label">{stat.label}</p>
                  <span className="ph-stat-icon" aria-hidden>
                    <Icon size={16} />
                  </span>
                </div>
                <p
                  className={`ph-stat-value ${stat.value == null ? "ph-stat-value--loading" : ""}`}
                >
                  {stat.value ?? "…"}
                </p>
                <p className="ph-stat-hint">{stat.hint}</p>
              </div>
            );
          })}
        </motion.section>

        {sectionsWithItems.length === 0 ? (
          <div className="ph-empty" role="status">
            No modules match &quot;{query}&quot;. Try another search term.
          </div>
        ) : (
          <div className="flex flex-col gap-10 sm:gap-12">
            {sectionsWithItems.map((section) => (
              <section key={section.id} className="ph-section" aria-labelledby={`ph-section-${section.id}`}>
                <div className="ph-section-head">
                  <div className="ph-section-title-row">
                    <h2 id={`ph-section-${section.id}`} className="ph-section-title">
                      {section.title}
                    </h2>
                    <span className="ph-section-count">
                      <ClipboardList size={12} aria-hidden />
                      {section.items.length}
                    </span>
                  </div>
                  <p className="ph-section-sub">{section.subtitle}</p>
                </div>

                <motion.div
                  className="ph-grid"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  key={`${section.id}-${query}`}
                >
                  {section.items.map((mod) => (
                    <motion.div key={mod.id} variants={itemVariants}>
                      <FeatureCard
                        title={mod.title}
                        description={mod.description}
                        icon={mod.icon}
                        badge={mod.badge}
                        variant="navy"
                        onClick={() => nav(mod.route)}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              </section>
            ))}
          </div>
        )}

        <p className="ph-footer-tip">
          <BarChart3 size={15} aria-hidden />
          <span>
            Stats refresh automatically every minute from your organisation database.
            Use search to jump to any payroll module.
          </span>
        </p>
      </div>
    </div>
  );
}
